import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FeFilterPreset } from 'graphql-common';
import _get from 'lodash/get';
import _omit from 'lodash/omit';
import { Values, Option } from '@lib/interfaces/form';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import Chip, { ChipSizes, ChipTypes } from '@lib/components/Chip/Chip';
import Button, {
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import DateRangePicker from '@lib/components/DateTimePicker/DateRangePicker';
import { InputSizes, InputTypes } from '@lib/components/Input/enums';
import Select from '@lib/components/Select/Select';
import Modal from '@lib/components/Modal/Modal';
import Form, { FormRef } from '@lib/components/ReactHookForm/FormContainer';
import formStyles from '@lib/assets/styles/Form.module.scss';
import { UseModalManagementResult } from '@lib/hooks/useModalManagement';
import MonitoringFilterFieldNames from '@lib/enums/fieldNames/monitoringFilterFieldNames';
import {
  MonitoringEntity,
  StatusKind,
} from 'routes/Dashboard/Monitoring/enums';
import ChipOption from '@lib/components/Select/components/ChipOption';
import ChipValueContainerForChipControl from '@lib/components/Select/components/ChipValueContainerForChipControl';
import ChipControl from '@lib/components/Select/components/ChipControl';
import { captureException } from '@lib/utils/captureException';
import toast from '@lib/components/Toast/Toast';
import { getGraphQLErrors } from '@lib/utils/graphQLErrors';
import getStatusOptions from '../utils/getStatusOptions';
import getStatusOption from '../utils/getStatusOption';
import { UseActionsResult } from '../utils/useActions';
import MonitoringFilterActions from './MonitoringFilterActions';
import getFilterFieldNameByMonitoringEntity from '../utils/getFilterFieldNameByMonitoringEntity';
import getMonitoringEntity from '../utils/getMonitoringEntity';
import useFilterFields from '../utils/useFilterFields';
import { UseQueryResult } from '../utils/useQueries';
import ApplyFilterPreset from '../ApplyFilterPreset/ApplyFilterPreset';
import { DASHBOARD_MONITORING_FILTER_FORM_EMPTY_VALUES } from '../constants';
import useFilterPresetFields, {
  DASHBOARD_MONITORING_FILTER_CREATE_FORM_VALUES,
} from '../utils/useFilterPresetFields';
import useMutations from '../utils/useMutations';
import createFilterPresetVariables from '../utils/createFilterPresetVariables';
import applyFilterPresetToFilters from '../utils/applyFilterPresetToFilters';
import FilterPresetFormActions from '../FilterPresetFormActions';
import styles from './MonitoringFilter.module.scss';

type Props = {
  filterParams: Values;
  modal: string;
  useActionsResult: UseActionsResult;
  useModalManagementResult: UseModalManagementResult;
  useQueriesResult: UseQueryResult;
};

export default function MonitoringFilter(props: Props) {
  const {
    filterParams,
    modal,
    useActionsResult,
    useModalManagementResult,
    useQueriesResult,
  } = props;
  const {
    basePeriodEndParam,
    basePeriodStartParam,
    defaultMaxDateObj: maxDate,
    defaultMinDateObj: minDate,
    onUpdateFilterParams,
    resetFilterParams,
    resetPeriodFilterParams,
  } = useActionsResult;
  const { t } = useTranslation();
  const formRef = useRef<FormRef>(undefined);
  const dateRangePickerRef = useRef(undefined);
  const createFormRef = useRef<FormRef>(undefined);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const { getFormCategoriesLoadOptions, getTemplatesLoadOptions } =
    useQueriesResult;

  const { isModalOpened, openModal, closeModal } = useModalManagementResult;
  const openFilter = () => openModal(modal);
  const closeFilter = () => closeModal(modal);
  const clearFilter = () => {
    closeModal(modal);
    resetFilterParams();
  };

  const monitoringEntity = getMonitoringEntity({ filterParams });
  const fieldNameFilterPresetId = getFilterFieldNameByMonitoringEntity(
    monitoringEntity,
    MonitoringFilterFieldNames.FilterPresetId,
  );
  const fieldNamePeriod = getFilterFieldNameByMonitoringEntity(
    monitoringEntity,
    MonitoringFilterFieldNames.Period,
  );
  const fieldNameStatus = getFilterFieldNameByMonitoringEntity(
    monitoringEntity,
    MonitoringFilterFieldNames.Status,
  );
  const fieldNameFormCategory = getFilterFieldNameByMonitoringEntity(
    monitoringEntity,
    MonitoringFilterFieldNames.FormCategory,
  );
  const fieldNameFormTemplateName = getFilterFieldNameByMonitoringEntity(
    monitoringEntity,
    MonitoringFilterFieldNames.FormTemplateName,
  );
  const status = _get(filterParams, fieldNameStatus) as Option;

  let filterCounter = 0;
  if (_get(filterParams, [fieldNameFormCategory, 'length'])) {
    filterCounter += 1;
  }
  if (_get(filterParams, [fieldNameFormTemplateName, 'length'])) {
    filterCounter += 1;
  }

  const filterPresetId = filterParams[fieldNameFilterPresetId] as
    string | undefined;
  const lastUpdate = filterParams[MonitoringFilterFieldNames.LastUpdate]
    ? `${filterParams[MonitoringFilterFieldNames.LastUpdate]}`
    : new Date().toISOString();
  const periodValue = (filterParams[fieldNamePeriod] as string[]) || [
    basePeriodStartParam,
    basePeriodEndParam,
  ];

  const onTimeFilterChange = (v: string[]) => {
    if (Array.isArray(v) && v[0] && v[1]) {
      onUpdateFilterParams({
        ...filterParams,
        [fieldNameFilterPresetId]: undefined,
        [fieldNamePeriod]: v,
      });
    } else {
      resetPeriodFilterParams();
    }
  };

  const defaultValues =
    filterParams && Object.keys(filterParams).length > 0
      ? filterParams
      : DASHBOARD_MONITORING_FILTER_FORM_EMPTY_VALUES;

  const fields = useFilterFields({
    fieldNameFormCategory,
    fieldNameFormTemplateName,
    getFormCategoriesLoadOptions,
    getTemplatesLoadOptions,
  });

  const onSubmitFilter = (values: Values) => {
    onUpdateFilterParams({
      ...values,
      [MonitoringFilterFieldNames.LastUpdate]: new Date().toISOString(),
      [fieldNameFilterPresetId]: undefined,
    });
    closeFilter();
  };

  const onCreateCallback = (
    filterPreset: FeFilterPreset | null | undefined,
  ) => {
    onUpdateFilterParams({
      [MonitoringFilterFieldNames.LastUpdate]: new Date().toISOString(),
      [fieldNameFilterPresetId]: filterPreset?.id,
    });
  };

  const openCreatePresetModal = () => setIsCreateModalOpen(true);
  const closeCreatePresetModal = () => setIsCreateModalOpen(false);

  const {
    fetchFeFilterPresetCreateMutation,
    fetchFeFilterPresetCreateMutationResult,
  } = useMutations();
  const { loading: isCreatingPreset, error: createPresetError } =
    fetchFeFilterPresetCreateMutationResult;

  const createFilterPresetFields = useFilterPresetFields({ monitoringEntity });

  const handleCreateFilterPresetSubmit = (values: Values) => {
    try {
      const variables = createFilterPresetVariables({
        values,
        filterParams,
        monitoringEntity,
      });

      fetchFeFilterPresetCreateMutation({
        variables,
        onCompleted: (res) => {
          toast({ content: t('create-filter-preset-success') });
          onCreateCallback(res?.data as FeFilterPreset);
          closeCreatePresetModal();
        },
      });
    } catch (error) {
      captureException(error);
    }
  };

  const onApplyCallback = (filterPreset: FeFilterPreset) => {
    if (!filterPreset) return;
    const statusKind = (_get(filterParams, [fieldNameStatus, 'value']) ||
      undefined) as StatusKind | undefined;
    const patch = applyFilterPresetToFilters({
      preset: filterPreset,
      monitoringEntity,
      statusKind,
    });
    onUpdateFilterParams(patch);
  };

  const onDeletePresetCallback = (destroyedFilterPresetId: string) => {
    if (filterPresetId === destroyedFilterPresetId) {
      onUpdateFilterParams({
        [fieldNameFilterPresetId]: undefined,
        [MonitoringFilterFieldNames.LastUpdate]: new Date().toISOString(),
      });
    }
  };

  let modalFilterTitle = t('filter');
  if (monitoringEntity === MonitoringEntity.Site) {
    modalFilterTitle = t('filter-sites');
  } else if (monitoringEntity === MonitoringEntity.Sector) {
    modalFilterTitle = t('filter-sectors');
  }

  let createModalCaption: string | undefined;
  if (monitoringEntity === MonitoringEntity.Site) {
    createModalCaption = t('save-current-sites-view');
  } else if (monitoringEntity) {
    createModalCaption = t('save-current-sectors-view');
  }

  const statusOptions = getStatusOptions({ t });
  const statusOption =
    getStatusOption({ t, status: status?.value })?.data || {};
  const chipControlProps = {
    leftIcon: _get(statusOption, 'leftIcon'),
    leftIconClassName: _get(statusOption, 'leftIconClassName'),
    leftIconComponent: _get(statusOption, 'leftIconComponent'),
    leftIconSymbolsOutlined: _get(statusOption, 'leftIconSymbolsOutlined'),
    size: ChipSizes.large,
    type: ChipTypes.chipFilter,
  };

  return (
    <>
      <PageBody className={styles.filterWrap}>
        <WhiteBox
          className={styles.filter}
          verticalPaddings="extraSmall"
          horizontalPaddings="small"
        >
          <div className={styles.filterRow}>
            <div>
              <Select
                name={fieldNameStatus}
                fullWidthMenu={false}
                size={InputSizes.medium}
                options={statusOptions}
                value={status}
                onChange={(v) =>
                  onUpdateFilterParams({
                    [fieldNameStatus]: _omit(v as object, 'data', 'label'),
                    [MonitoringFilterFieldNames.LastUpdate]:
                      new Date().toISOString(),
                  })
                }
                isClearable={false}
                isSearchable={false}
                components={{
                  Option: ChipOption,
                  Control: ChipControl,
                  ValueContainer: ChipValueContainerForChipControl,
                }}
                chipControlProps={chipControlProps}
                usePortal
              />
            </div>
            <div>
              <ApplyFilterPreset
                lastUpdate={lastUpdate}
                onApplyCallback={onApplyCallback}
                onDeletePresetCallback={onDeletePresetCallback}
                filterPresetId={filterPresetId}
                monitoringEntity={monitoringEntity}
                onCreateClick={openCreatePresetModal}
                key={monitoringEntity}
                useQueriesResult={useQueriesResult}
              />
            </div>
            <div>
              <Chip
                counter={filterCounter}
                label={t('filter')}
                leftIcon="filter_alt"
                leftIconSymbolsOutlined
                onClick={openFilter}
                size={ChipSizes.large}
                type={ChipTypes.chipFilter}
                tooltipForCroppedTextDisabled
              />
            </div>
            {!!filterCounter && (
              <div>
                <Button
                  buttonSize={ButtonSizes.large}
                  buttonText={t('reset')}
                  buttonType={ButtonTypes.primaryText}
                  onClick={clearFilter}
                />
              </div>
            )}
          </div>
          <div className={styles.dateRangePickerCol}>
            <DateRangePicker
              label={t('period')}
              onChange={onTimeFilterChange}
              ref={dateRangePickerRef}
              size={InputSizes.medium}
              type={InputTypes.dateRange}
              value={periodValue}
              minDate={minDate}
              maxDate={maxDate}
            />
          </div>
        </WhiteBox>
      </PageBody>
      <Modal
        isOpen={isModalOpened(modal)}
        caption={modalFilterTitle}
        onClose={closeFilter}
      >
        <Form
          formRef={formRef}
          fields={fields}
          onSubmit={onSubmitFilter}
          defaultValues={defaultValues}
          actionsComponent={MonitoringFilterActions}
          actionsComponentProps={{
            onCancel: closeFilter,
          }}
          formClassName={formStyles.form}
        />
      </Modal>
      <Modal
        isOpen={isCreateModalOpen}
        onClose={closeCreatePresetModal}
        caption={createModalCaption}
      >
        <Form
          actionsComponent={FilterPresetFormActions}
          actionsComponentProps={{ onCancel: closeCreatePresetModal }}
          defaultValues={DASHBOARD_MONITORING_FILTER_CREATE_FORM_VALUES}
          fields={createFilterPresetFields}
          formId="create-filter-preset"
          formRef={createFormRef}
          loading={isCreatingPreset}
          graphQLErrors={getGraphQLErrors(createPresetError)}
          onSubmit={handleCreateFilterPresetSubmit}
        />
      </Modal>
    </>
  );
}
